import { z } from 'zod';
import { PricingTierSchema } from './common';

// ─── BOM line items ──────────────────────────────────────────────────

export const LABOR_CLASSIFICATIONS = [
  'JOURNEYMAN', 'APPRENTICE', 'LEAD_FOREMAN', 'PROJECT_MANAGER', 'PROGRAMMER', 'HELPER',
] as const;
export const LaborClassificationSchema = z.enum(LABOR_CLASSIFICATIONS);
export type LaborClassification = z.infer<typeof LaborClassificationSchema>;

export const BomItemCreateSchema = z.object({
  system: z.string().min(1).max(128),
  subsystem: z.string().max(128).optional().nullable(),
  category: z.string().max(128).optional().nullable(),
  name: z.string().min(1).max(256),
  description: z.string().max(1024).optional().nullable(),
  manufacturer: z.string().max(128).optional().nullable(),
  partNumber: z.string().max(128).optional().nullable(),
  quantity: z.number().nonnegative(),
  unit: z.string().max(16).default('EA'),
  unitPrice: z.number().nonnegative(),
  pricingTier: PricingTierSchema.optional().nullable(),
  overridePrice: z.number().nonnegative().optional().nullable(),
  overrideReason: z.string().max(512).optional().nullable(),
});
export type BomItemCreate = z.infer<typeof BomItemCreateSchema>;

export const BomItemUpdateSchema = BomItemCreateSchema.partial();
export type BomItemUpdate = z.infer<typeof BomItemUpdateSchema>;

// ─── Labor line items ────────────────────────────────────────────────

export const LaborItemCreateSchema = z.object({
  activityCategory: z.string().min(1).max(128),
  description: z.string().max(1024).optional().nullable(),
  classification: LaborClassificationSchema.default('JOURNEYMAN'),
  hours: z.number().nonnegative(),
  baseRate: z.number().nonnegative(),
  burdenPct: z.number().min(0).max(1).default(0),
  overheadPct: z.number().min(0).max(1).default(0),
  markupPct: z.number().min(0).max(1).default(0),
  productivityMultiplier: z.number().positive().default(1.0),
  notes: z.string().max(1024).optional().nullable(),
});
export type LaborItemCreate = z.infer<typeof LaborItemCreateSchema>;

export const LaborItemUpdateSchema = LaborItemCreateSchema.partial();
export type LaborItemUpdate = z.infer<typeof LaborItemUpdateSchema>;

// ─── Helpers ─────────────────────────────────────────────────────────

// Extended price = override_price when set, else unit_price × quantity.
// Kept in @ub/shared so API + web agree on the math.
export function computeExtendedPrice(
  unitPrice: number,
  quantity: number,
  overridePrice: number | null | undefined,
): number {
  const effective = overridePrice != null ? overridePrice : unitPrice;
  return +(effective * quantity).toFixed(2);
}

// Labor total = hours × base_rate × productivity_multiplier × (1 + burden + overhead + markup).
// Same formula engines/labor.ts uses, lifted here so the UI can preview totals live.
export function computeLaborTotal(
  hours: number,
  baseRate: number,
  burdenPct: number,
  overheadPct: number,
  markupPct: number,
  productivityMultiplier: number,
): number {
  const base = hours * baseRate * productivityMultiplier;
  const multiplier = 1 + burdenPct + overheadPct + markupPct;
  return +(base * multiplier).toFixed(2);
}
